import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../lib/supabase';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import Timeline from '../components/Timeline';
import { ArrowLeft, Plus, Copy, XCircle, Nfc } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export default function CaseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updateData, setUpdateData] = useState({ status: '', note: '' });
  const [submitting, setSubmitting] = useState(false);
  const [showAddUpdate, setShowAddUpdate] = useState(false);
  const [nfcToken, setNfcToken] = useState(null);
  const [generatingToken, setGeneratingToken] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/clinic/login');
      return;
    }
    if (user.role !== 'clinic_staff') {
      navigate('/owner');
      return;
    }
    fetchCase();
  }, [id, user, navigate]);

  const fetchCase = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_URL}/api/clinic/cases/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCaseData(response.data);
    } catch (error) {
      toast.error('Error al cargar el caso');
      navigate('/clinic/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUpdate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API_URL}/api/clinic/cases/${id}/updates`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('¡Actualización agregada!');
      setUpdateData({ status: '', note: '' });
      setShowAddUpdate(false);
      fetchCase();
    } catch (error) {
      toast.error('Error al agregar actualización');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseCase = async () => {
    if (!window.confirm('¿Estás seguro de cerrar este caso? Los códigos NFC pendientes expirarán.')) {
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API_URL}/api/clinic/cases/${id}/close`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Caso cerrado exitosamente');
      navigate('/clinic/dashboard');
    } catch (error) {
      toast.error('Error al cerrar el caso');
    }
  };

  const handleGenerateNFCToken = async () => {
    setGeneratingToken(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_URL}/api/clinic/cases/${id}/generate-nfc-token`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNfcToken(response.data);
      toast.success('Código NFC generado exitosamente');
    } catch (error) {
      toast.error('Error al generar código NFC');
    } finally {
      setGeneratingToken(false);
    }
  };

  const copyNFCLink = () => {
    if (!nfcToken) return;
    const link = `${window.location.origin}/nfc/${nfcToken.token}`;
    navigator.clipboard.writeText(link);
    toast.success('Link NFC copiado al portapapeles');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p>Cargando...</p>
      </div>
    );
  }

  if (!caseData) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/clinic/dashboard')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver al Dashboard
        </Button>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Left Column - Case Info */}
          <div className="md:col-span-1 space-y-6">
            {/* Pet Info Card */}
            <Card className="p-6 bg-white border border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Información</h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Mascota</p>
                  <p className="text-lg font-semibold text-slate-900">{caseData.pet.name}</p>
                  <p className="text-sm text-slate-600">
                    {caseData.pet.species} {caseData.pet.breed && `- ${caseData.pet.breed}`}
                  </p>
                  {caseData.pet.age && (
                    <p className="text-sm text-slate-600">{caseData.pet.age} años</p>
                  )}
                </div>

                <div className="border-t border-slate-200 pt-4">
                  <p className="text-sm text-slate-500 mb-1">Dueño</p>
                  <p className="font-medium text-slate-900">{caseData.owner.full_name}</p>
                  <p className="text-sm text-slate-600">{caseData.owner.phone}</p>
                  {caseData.owner.email && (
                    <p className="text-sm text-slate-600">{caseData.owner.email}</p>
                  )}
                </div>

                <div className="border-t border-slate-200 pt-4">
                  <p className="text-sm text-slate-500 mb-1">Razón de Ingreso</p>
                  <p className="text-sm text-slate-700">{caseData.reason}</p>
                </div>

                <div className="border-t border-slate-200 pt-4">
                  <p className="text-sm text-slate-500 mb-1">Fecha de Ingreso</p>
                  <p className="text-sm text-slate-700">
                    {format(new Date(caseData.created_at), "d 'de' MMMM 'de' yyyy", { locale: es })}
                  </p>
                </div>
              </div>
            </Card>

            {/* NFC Token Card */}
            <Card className="p-6 bg-blue-50 border border-blue-100">
              <div className="flex items-center gap-2 mb-3">
                <Nfc className="h-5 w-5 text-blue-600" />
                <h3 className="font-semibold text-slate-900">Código NFC</h3>
              </div>
              
              {!nfcToken ? (
                <div>
                  <p className="text-sm text-slate-600 mb-3">
                    Genera un código NFC para que el dueño vincule su mascota
                  </p>
                  <Button 
                    onClick={handleGenerateNFCToken} 
                    className="w-full" 
                    size="sm"
                    disabled={generatingToken}
                    data-testid="generate-nfc-button"
                  >
                    {generatingToken ? 'Generando...' : 'Generar Código NFC'}
                  </Button>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-slate-600 mb-2">Comparte este link con el dueño:</p>
                  <div className="bg-white rounded-lg p-3 border border-slate-200 mb-3">
                    <p className="text-xs text-slate-500 break-all font-mono">
                      {`${window.location.origin}/nfc/${nfcToken.token}`}
                    </p>
                  </div>
                  <Button 
                    onClick={copyNFCLink} 
                    className="w-full" 
                    variant="outline" 
                    size="sm"
                    data-testid="copy-nfc-link-button"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copiar Link
                  </Button>
                  <p className="text-xs text-slate-500 mt-2">
                    Expira: {format(new Date(nfcToken.expires_at), "d 'de' MMMM, HH:mm", { locale: es })}
                  </p>
                </div>
              )}
            </Card>

            {/* Actions */}
            <Card className="p-6 bg-white border border-slate-200">
              <Dialog open={showAddUpdate} onOpenChange={setShowAddUpdate}>
                <DialogTrigger asChild>
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 mb-3" data-testid="add-update-button">
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar Actualización
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nueva Actualización</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddUpdate} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Estado
                      </label>
                      <Select
                        value={updateData.status}
                        onValueChange={(value) => setUpdateData({...updateData, status: value})}
                        required
                      >
                        <SelectTrigger data-testid="update-status-select">
                          <SelectValue placeholder="Seleccionar estado" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Recibido">Recibido</SelectItem>
                          <SelectItem value="En evaluación">En Evaluación</SelectItem>
                          <SelectItem value="En procedimiento">En Procedimiento</SelectItem>
                          <SelectItem value="En recuperación">En Recuperación</SelectItem>
                          <SelectItem value="Listo para recoger">Listo para Recoger</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Nota
                      </label>
                      <Textarea
                        value={updateData.note}
                        onChange={(e) => setUpdateData({...updateData, note: e.target.value})}
                        placeholder="Describe el estado actual..."
                        required
                        rows={4}
                        data-testid="update-note-input"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" disabled={submitting} className="flex-1" data-testid="submit-update-button">
                        {submitting ? 'Guardando...' : 'Guardar'}
                      </Button>
                      <Button type="button" variant="outline" onClick={() => setShowAddUpdate(false)}>
                        Cancelar
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {caseData.is_active && (
                <Button
                  onClick={handleCloseCase}
                  variant="destructive"
                  className="w-full"
                  data-testid="close-case-button"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Cerrar Caso
                </Button>
              )}
            </Card>
          </div>

          {/* Right Column - Timeline */}
          <div className="md:col-span-2">
            <div className="bg-white rounded-xl border border-slate-200 p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-8">Timeline de Actualizaciones</h2>
              {caseData.updates && caseData.updates.length > 0 ? (
                <Timeline updates={caseData.updates.reverse()} />
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <p>No hay actualizaciones aún</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
